import type { CombinedOrderReport, SkuReportItem } from "@/features/profit-calculators/calculator-shared/types"

export const SKU_CATEGORIES = ["ALL", "HIGH", "MEDIUM", "LOW", "LOSS"] as const

export const formatCurrency = (amount: number, showSign = false): string => {
  const formatted = `₹${Math.abs(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
  return showSign && amount < 0 ? `-${formatted}` : formatted
}

export const getOrderStatuses = (platform: "meesho" | "amazon"): string[] =>
  platform === "amazon"
    ? ["ALL", "DELIVERED", "RETURNED", "CANCELLED"]
    : ["ALL", "CANCELLED", "CLAIMED", "DELIVERED", "EXCHANGED", "RETURNED", "RTO COMPLETE", "RTO LOCKED"]

export const getStatusColor = (status: string) => {
  const colors: Record<string, { bg: string; color: string }> = {
    DELIVERED: { bg: "#e2fbe8", color: "#1b5e20" },
    RETURNED: { bg: "#fff3e0", color: "#e65100" },
    CANCELLED: { bg: "#f5f5f5", color: "#616161" },
    "RTO COMPLETE": { bg: "#ffebee", color: "#c62828" },
    CLAIMED: { bg: "#e3f2fd", color: "#0d47a1" },
    EXCHANGED: { bg: "#f3e5f5", color: "#4a148c" },
    "RTO LOCKED": { bg: "#eceff1", color: "#37474f" }
  }
  return {
    ...(colors[status] ?? { bg: "#eceff1", color: "#455a64" }),
    label: status
  }
}

const escapeCsv = (value: string): string => `"${value.replace(/"/g, '""')}"`

const downloadCsv = (fileName: string, contents: string): void => {
  const url = URL.createObjectURL(new Blob([contents], { type: "text/csv;charset=utf-8;" }))
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

export const downloadSkuReport = (rows: SkuReportItem[], reportId: string): void => {
  const header =
    "SKU,Product Name,Settlement (INR),Purchase Cost (INR),Units Sold,Units Returned,Net Profit (INR),Performance Category\n"
  const body = rows
    .map(row =>
      [
        escapeCsv(row.sku),
        escapeCsv(row.productName),
        row.settlement.toFixed(2),
        row.purchaseCost.toFixed(2),
        row.unitsSold,
        row.unitsReturned,
        row.profit.toFixed(2),
        row.category
      ].join(",")
    )
    .join("\n")
  downloadCsv(`SKU_Profit_Report_${reportId}.csv`, header + body)
}

export const downloadOrderReport = (rows: CombinedOrderReport[], reportId: string): void => {
  const header =
    "Order ID,Product Name,SKU,Order Date,Normalized Status,Settlement (INR),Purchase Cost (INR),Profit/Loss (INR)\n"
  const body = rows
    .map(row =>
      [
        escapeCsv(row.subOrderNo),
        escapeCsv(row.productName),
        escapeCsv(row.sku),
        escapeCsv(row.orderDate),
        escapeCsv(row.liveOrderStatus),
        row.settlementAmount.toFixed(2),
        row.cogs.toFixed(2),
        row.profit.toFixed(2)
      ].join(",")
    )
    .join("\n")
  downloadCsv(`Order_Profit_Report_${reportId}.csv`, header + body)
}
